import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import clsx from "clsx";
import {
  CommentIcon,
  // FootballIcon,
  VectorIcon,
} from "@/shared/assets/svg/icons";
import { UserBubble } from "@shared/ui";
import { journalsQueries } from "../../../api/journals.queries";
import "./journals-responsible-table.scss";

interface IJournalsResponsibleTable {
  titleId?: string;
  sectionOneId?: string;
  sectionTwoId?: string;
  sectionThreeId?: string;
  sectionFourId?: string;
  sectionFiveId?: string;
  sectionSixId?: string;
  type?:
    | "title"
    | "sectionOne"
    | "sectionTwo"
    | "sectionThree"
    | "sectionFour"
    | "sectionFive"
    | "sectionSix";
}

export interface DataResponsibleType {
  key: string;
  name: string;
  role: string;
  position: string;
  email: string;
  phone: string;
  isBall?: boolean;
  isComment?: boolean;
}

const roleMapping: Record<string, string> = {
  inputters: "Заполнение данных",
  approvers: "Согласующий",
  signers: "Подписант",
  senders: "Отправка в ГСН",
};

const ADMIN_ROLE_LABEL = "Админ журнала";

const getQueryByType = ({
  titleId = "",
  sectionOneId = "",
  type,
  sectionTwoId = "",
  sectionThreeId = "",
  sectionFourId = "",
  sectionFiveId = "",
  sectionSixId = "",
}: {
  titleId?: string;
  sectionOneId?: string;
  sectionTwoId?: string;
  type:
    | "title"
    | "sectionOne"
    | "sectionTwo"
    | "sectionThree"
    | "sectionFour"
    | "sectionFive"
    | "sectionSix";
  sectionThreeId?: string;
  sectionFourId?: string;
  sectionFiveId?: string;
  sectionSixId: string;
}) => {
  switch (type) {
    case "title":
      return journalsQueries.getResponsibleCommentList(titleId!);
    case "sectionOne":
      return journalsQueries.getResponsibleCommentListFirstOne(sectionOneId!);
    case "sectionTwo":
      return journalsQueries.getResponsibleCommentListSecondOne(sectionTwoId!);
    case "sectionThree":
      return journalsQueries.getResponsibleCommentListThirdOne(sectionThreeId!);
    case "sectionFour":
      return journalsQueries.getResponsibleCommentListFourthOne(sectionFourId!);
    case "sectionFive":
      return journalsQueries.getResponsibleCommentListFifthOne(sectionFiveId!);
    case "sectionSix":
      return journalsQueries.getResponsibleCommentListSixthOne(sectionSixId!);
    default:
      return journalsQueries.getResponsibleCommentList(titleId!);
  }
};

export const JournalsResponsibleTable = ({
  titleId,
  sectionOneId,
  sectionTwoId,
  type,
  sectionThreeId,
  sectionFourId,
  sectionFiveId,
  sectionSixId,
}: IJournalsResponsibleTable) => {
  const queryClient = useQueryClient();
  const resolvedType = type || "title";
  const [expanded, setExpanded] = useState(false);

  const resourceIdReady =
    (resolvedType === "title" && !!titleId) ||
    (resolvedType === "sectionOne" && !!sectionOneId) ||
    (resolvedType === "sectionTwo" && !!sectionTwoId) ||
    (resolvedType === "sectionThree" && !!sectionThreeId) ||
    (resolvedType === "sectionFour" && !!sectionFourId) ||
    (resolvedType === "sectionFive" && !!sectionFiveId) ||
    (resolvedType === "sectionSix" && !!sectionSixId);

  const query = getQueryByType({
    titleId: titleId || "",
    sectionOneId: sectionOneId || "",
    sectionTwoId: sectionTwoId || "",
    sectionThreeId: sectionThreeId || "",
    sectionFourId: sectionFourId || "",
    sectionFiveId: sectionFiveId || "",
    sectionSixId: sectionSixId || "",
    type: resolvedType,
  });
  const { data, isPending: isResponsibleListPending, isFetching: isResponsibleListFetching } =
    // @ts-expect-error union query options по типу раздела
    useQuery({
      ...query,
      enabled: resourceIdReady,
      staleTime: 5 * 60_000,
    });

  const { data: titleData, isPending: isTitleJournalPending, isFetching: isTitleJournalFetching } =
    useQuery<any>({
      ...(journalsQueries.getTitleJournal({ titleId: titleId || "" }) as any),
      enabled: resolvedType === "title" && !!titleId && expanded,
    });
  const mainJournalId = titleData?.mainJournal?.mainJournalId || "";
  const { data: adminList, isPending: isAdminPending, isFetching: isAdminFetching } = useQuery<any>(
    {
      ...(journalsQueries.getResponsibleAdminList({ mainJournalId }) as any),
      enabled: resolvedType === "title" && !!mainJournalId && expanded,
    },
  );

  const isTableLoading =
    expanded &&
    (isResponsibleListPending ||
      isResponsibleListFetching ||
      (resolvedType === "title" &&
        (isTitleJournalPending ||
          isTitleJournalFetching ||
          isAdminPending ||
          isAdminFetching)));

  const mapDataToTable = (dataComment: any): DataResponsibleType[] => {
    if (!dataComment) return [];

    const rolesOrder = ["inputters", "approvers", "signers", "senders"] as const;

    return rolesOrder
      .flatMap((key) =>
        (dataComment?.[key] || []).map((item: any) => {
          if (!item) return null;

          const user = item.userHistory || {};
          const roleInfo = user.roleInfo || {};
          const companyHistory = roleInfo.userCompaniesHistory || [];

          const row: DataResponsibleType & {
            isResponsibility?: boolean;
            comment?: string;
          } = {
            key: user.userId,
            name: `${user.userSurname ?? ""} ${user.userFirstName ?? ""} ${user.userMiddleName ?? ""}`.trim(),
            role: roleMapping[key] || key,
            position: companyHistory?.[0]?.position || "—",
            email: user.userEmail || "—",
            phone: user.userMobilePhone || "—",
            isBall: item.isResponsibility ?? false,
          };
          row.isResponsibility = item.isResponsibility ?? false;
          row.comment = item.comment ?? null;

          return row;
        }),
      )
      .filter(Boolean) as DataResponsibleType[];
  };

  const mapAdminsToTable = (admins: any): DataResponsibleType[] => {
    if (!Array.isArray(admins)) return [];

    return admins
      .map((item: any) => {
        const user = item?.userHistory || {};
        const roleInfo = user?.roleInfo || {};
        const companyHistory = roleInfo?.userCompaniesHistory || [];

        if (!user?.userId) return null;

        return {
          key: user.userId,
          name: `${user.userSurname ?? ""} ${user.userFirstName ?? ""} ${user.userMiddleName ?? ""}`.trim(),
          role: ADMIN_ROLE_LABEL,
          position: companyHistory?.[0]?.position || "—",
          email: user.userEmail || "—",
          phone: user.userMobilePhone || "—",
        } satisfies DataResponsibleType;
      })
      .filter(Boolean) as DataResponsibleType[];
  };

  const tableData = [
    ...mapDataToTable(data),
    ...(resolvedType === "title" ? mapAdminsToTable(adminList) : []),
  ];

  const columns: ColumnsType<DataResponsibleType> = [
    {
      title: "ФИО",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div
          className={clsx(
            "journal-expanded-name",
            record.isBall && "journal-expanded-name--ball",
          )}
        >
          <UserBubble userId={record.key}>
            <Button
              type="link"
              style={record.isBall ? { paddingRight: 0 } : {}}
            >
              {text}
            </Button>
          </UserBubble>
          {/* {record.isBall ? (
            <FootballIcon style={{ marginRight: "4px" }} />
          ) : null} */}
        </div>
      ),
    },
    {
      title: "Роль",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Должность",
      dataIndex: "position",
      key: "position",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
      render: (text, record) => (
        <div className="journal-expanded-name">
          <div>{text}</div>
          {record.isComment ? (
            <Tooltip title="Какое-то сообщение от ответственного лица">
              <CommentIcon style={{ marginLeft: "16px", cursor: "pointer" }} />
            </Tooltip>
          ) : null}
        </div>
      ),
    },
    {
      title: "",
      key: "comment",
      width: 50,
      render: (_, record: any) =>
        record.comment ? (
          <Tooltip title={record.comment}>
            <div>
              <CommentIcon style={{ cursor: "pointer" }} />
            </div>
          </Tooltip>
        ) : null,
    },
  ];

  const handleExpand = () => {
    if (!expanded) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
    setExpanded(!expanded);
  };

  return (
    <div
      className="table-container table-container-scroll-tab journals-responsible-table"
      id="journals-responsible-table"
    >
      <div onClick={handleExpand} className="table-header">
        <span
          className={clsx(
            "expandable-icon-container",
            expanded && "expandable-icon-container--expanded",
          )}
        >
          <div className={clsx("expandable-icon", expanded && "open")}>
            <VectorIcon />
          </div>
          <div className="expandable-icon-container__title">Ответственные лица</div>
        </span>
      </div>
      <div
        className={clsx("table-body", expanded ? "open" : "closed", "table-journal")}
      >
        <Table
          loading={isTableLoading}
          columns={columns}
          dataSource={tableData}
          pagination={false}
          bordered={false}
          rowClassName={({ key }) => (key === "2" ? "table-row-grey" : "")}
        />
      </div>
    </div>
  );
};
